import { useMemo } from "react";
import { parseEmailImages } from "@tellescope/utils";
import type { ChatAttachment } from "@tellescope/types-models";
import { useEmailAttachments } from "./useEmailAttachments";

interface UseEmailContentOptions {
  html: string;
  attachments?: ChatAttachment[];
}

export interface AttachmentWithUrl extends ChatAttachment {
  downloadUrl?: string;
}

interface UseEmailContentReturn {
  processedHtml: string;
  isProcessing: boolean;
  unmatchedAttachments: AttachmentWithUrl[];
}

export const useEmailContent = ({ html, attachments }: UseEmailContentOptions): UseEmailContentReturn => {
  // Parse HTML to find and match images to attachments
  const imageMatches = useMemo(() => parseEmailImages(html, attachments), [html, attachments]);

  // Get the set of secureName values for attachments matched to images
  const matchedSecureNames = useMemo(() => {
    const names = new Set<string>();
    imageMatches.forEach((match) => {
      if (match.attachment) {
        names.add(match.attachment.secureName);
      }
    });
    return names;
  }, [imageMatches]);

  // Fetch download URLs for ALL attachments (both matched and unmatched)
  const { urlMap, isLoading } = useEmailAttachments(attachments);

  // Get unmatched attachments with their download URLs
  const unmatchedAttachments = useMemo(() => {
    if (!attachments) return [];

    return attachments
      .filter((att) => !matchedSecureNames.has(att.secureName))
      .map((att) => ({
        ...att,
        downloadUrl: urlMap.get(att.secureName),
      }));
  }, [attachments, matchedSecureNames, urlMap]);

  // Process HTML by injecting URLs for matched images and removing unmatched ones
  const processedHtml = useMemo(() => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const images = doc.querySelectorAll("img");

      let removedCount = 0;

      images.forEach((img, index) => {
        // Remove Tellescope tracking pixels (check if pathname starts with /email-image/)
        const src = img.getAttribute("src") || "";
        if (src) {
          try {
            const url = new URL(src, window.location.origin); // Use base URL for relative URLs
            if (url.pathname.startsWith("/email-image/")) {
              img.remove();
              removedCount++;
              return;
            }
          } catch {
            // Invalid URL, continue processing normally
          }
        }

        // Adjust index to account for removed tracking pixels
        const adjustedIndex = index - removedCount;
        const match = imageMatches[adjustedIndex];
        if (!match) return;

        if (match.attachment) {
          // Image matched to an attachment
          const downloadURL = urlMap.get(match.attachment.secureName);

          if (downloadURL) {
            // We have the download URL, inject it as src
            img.setAttribute("src", downloadURL);
          } else if (!isLoading) {
            // URL fetch failed, replace img with alt text
            const textNode = doc.createTextNode(match.altText || "");
            img.replaceWith(textNode);
          }
          // If still loading, keep the img tag as-is
        } else {
          // Image not matched to any attachment, replace with alt text
          const textNode = doc.createTextNode(match.altText || "");
          img.replaceWith(textNode);
        }
      });

      // Clean up trailing <br> tags recursively
      const cleanTrailingBrTags = (element: Element, depth = 0, maxDepth = 50) => {
        if (depth >= maxDepth) return;

        let lastChild = element.lastChild;
        while (lastChild) {
          if (lastChild.nodeType === Node.ELEMENT_NODE && (lastChild as Element).tagName.toLowerCase() === "br") {
            const prev = lastChild.previousSibling;
            lastChild.remove();
            lastChild = prev;
          } else if (lastChild.nodeType === Node.TEXT_NODE && lastChild.textContent?.trim() === "") {
            // Also remove trailing empty text nodes
            const prev = lastChild.previousSibling;
            lastChild.remove();
            lastChild = prev;
          } else if (lastChild.nodeType === Node.ELEMENT_NODE) {
            // Recursively clean the last element child
            cleanTrailingBrTags(lastChild as Element, depth + 1, maxDepth);
            break;
          } else {
            break;
          }
        }
      };

      cleanTrailingBrTags(doc.body);

      return doc.body.innerHTML;
    } catch (error) {
      console.error("Error processing email content:", error);
      return html;
    }
  }, [html, imageMatches, urlMap, isLoading]);

  return {
    processedHtml,
    isProcessing: isLoading,
    unmatchedAttachments,
  };
};
